import { useState, useEffect, useContext } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  VStack,
  Button,
  Spinner,
  useToast,
  Heading,
} from '@chakra-ui/react';
import axios from 'axios';
import { Select } from 'chakra-react-select';
import { POOl_RANKING, TARM_AWS_URL } from '../../api/api';
import Loading from '../Loading/Loading';
import { AccountContext } from '../../context/AccountContext';
import { getRunID } from '../../util/runID';
import { useNavigate } from 'react-router-dom';
import { removeKeys, serializeDataForSelect } from '../../util/utility';

const SubmitPoolRankingRequest = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [source] = useState(axios.CancelToken.source());
  const [selectedJobProfiles, setSelectedJobProfiles] = useState([]);
  const [run_id] = useState(getRunID());
  const [allJobProfiles, setAllJobProfiles] = useState([]);
  const [selectedResumePool, setSelectedResumePool] = useState(null);
  const [allResumePools, setAllResumePools] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [careerStage, setCareerStage] = useState(null);
  const { user } = useContext(AccountContext);
  const [destinationEmail, setDestinationEmail] = useState([
    { label: user.email, value: user.email, isFixed: true },
  ]);
  const [sesIds, setSesIds] = useState([]);

  const careerStages = [
    'manager',
    'lead',
    'senior associate',
    'senior',
    'associate',
    'junior',
  ];

  useEffect(() => {
    fetchJdsPoolsAndSesIds();
    return () => {
      if (source) {
        source.cancel();
      }
    };
  }, []);

  const fetchJdsPoolsAndSesIds = async () => {
    try {
      const [jobProfilesResponse, allResumesPoolsResponse, sesIdsResponse] =
        await Promise.all([
          TARM_AWS_URL.get('jd-parser/get-db-items?identifier=jds', {
            cancelToken: source.token,
          }),
          TARM_AWS_URL.get('jd-parser/get-db-items?identifier=pools', {
            cancelToken: source.token,
          }),
          TARM_AWS_URL.get('get-ses-ids', { cancelToken: source.token }),
        ]);
      setAllJobProfiles(serializeDataForSelect(jobProfilesResponse.data, 'PK'));
      setAllResumePools(
        serializeDataForSelect(allResumesPoolsResponse.data, 'pool_name')
      );
      setSesIds(sesIdsResponse.data);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false); // Stop loading after fetching
    }
  };

  const handleError = (error) => {
    console.error(error); // Log error to console for debugging
    toast({
      title: error.message,
      status: 'error',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleResumePoolChange = (selectedItem) => {
    setSelectedResumePool(selectedItem);
  };

  const handleJobProfileChange = (selectedItems) => {
    if (selectedItems.length <= 10) {
      setSelectedJobProfiles(selectedItems);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(selectedJobProfiles.length && selectedResumePool)) return;

    setIsBtnLoading(true);
    const pool = removeKeys(selectedResumePool, ['label', 'value']);
    const jds = removeKeys(selectedJobProfiles, ['label', 'value']);
    const destination_email = destinationEmail
      .map((obj) => obj.value)
      .join(',');

    const postBody = {
      pool_name: pool['pool_name'] + '_' + run_id,
      pool: pool,
      author_email: user.email,
      destination_email: destination_email,
      job_title: careerStage.value,
      jds: jds,
      run_id: run_id,
    };

    try {
      await POOl_RANKING.post(
        'configure-step-function',
        JSON.stringify(postBody),
        {
          cancelToken: source.token,
        }
      );
      navigate('/home/success', {
        state: {
          redirectURL: `/home/pool-ranking/progress/${user.email}/${run_id}`,
          replace: true,
        },
      });
    } catch (error) {
      console.error(error);
      const errorMessage = error.response?.data
        ? error.response?.data
        : error.message;

      toast({
        title: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsBtnLoading(false);
    }
  };

  if (isLoading) {
    return <Loading loading={isLoading} />;
  }

  return (
    <VStack>
      <Heading>Rank resume pool against multiple job profiles</Heading>
      <br />
      <Box
        borderWidth="3px"
        borderRadius="lg"
        p="10"
        width="700px"
        height="75vh"
        overflowY="scroll"
      >
        <form onSubmit={handleSubmit}>
          <FormControl isRequired mt={4}>
            <FormLabel>Select resume pool</FormLabel>
            <Select
              isMulti={false}
              name="resumePool"
              options={allResumePools}
              placeholder="Search"
              value={selectedResumePool}
              closeMenuOnSelect={true}
              onChange={handleResumePoolChange}
            />
          </FormControl>

          <FormControl isRequired mt={4}>
            <FormLabel htmlFor="careerStages">Select career stage</FormLabel>
            <Select
              id="careerStages"
              name="careerStage"
              options={careerStages.map((stage) => ({
                label: stage,
                value: stage,
              }))}
              value={careerStage}
              onChange={setCareerStage}
              placeholder="Search career stages"
              closeMenuOnSelect={true}
            />
          </FormControl>

          <FormControl isRequired mt={4}>
            <FormLabel>Select job profiles (max 10)</FormLabel>
            <Select
              isMulti={true}
              name="jobProfiles"
              options={allJobProfiles}
              placeholder="Search"
              closeMenuOnSelect={false}
              value={selectedJobProfiles}
              onChange={handleJobProfileChange}
            />
          </FormControl>

          <FormControl isRequired mt={4}>
            <FormLabel htmlFor="sesIds">
              Select email ids to send reports to
            </FormLabel>
            <Select
              isMulti={true}
              id="sesIds"
              name="sesIds"
              options={sesIds.map((stage) => ({ label: stage, value: stage }))}
              value={destinationEmail}
              onChange={setDestinationEmail}
              placeholder="Select email to send reports"
              closeMenuOnSelect={false}
            />
          </FormControl>

          <Box textAlign="end" mt="4">
            <Button
              type="submit"
              colorScheme="blue"
              disabled={
                !(
                  selectedResumePool &&
                  careerStage &&
                  selectedJobProfiles.length &&
                  destinationEmail.length
                ) || isBtnLoading
              }
            >
              {isBtnLoading && <Spinner size="sm" mr={2} />}
              Submit
            </Button>
          </Box>
        </form>
      </Box>
    </VStack>
  );
};

export default SubmitPoolRankingRequest;
